import { useCallback, type RefCallback } from 'react'

/*
 * 상자 크기를 CSS 로 넘기는 끈 둘 — 컨테이너 단위 · 컨테이너 쿼리 대신(2026-10-28 · 모니터링 칸 접기/펼치기 · 장비 칩 랙).
 * 🔴 둘 다 «콜백 ref» 다 — 요소가 나중에 서도(접힌 칸을 펼침 · 랙이 늦게 옴) 그때 관찰을 건다. 떼면 React 가 정리 함수를 부른다.
 * ⚠ `ResizeObserver` 가 없는 곳(jsdom)에서는 아무것도 안 한다.
 */

/**
 * **상자의 내용 키를 CSS 변수 하나로 건다.**
 *
 * 🔴 **컨테이너 단위(`100cqh`)를 대신한다.** 크기 컨테이너 안에서 그림 폭 · 행 키가 그 컨테이너의 키를 `cqh` 로 읽으면,
 *    바깥 격자 · flex 가 그 상자를 «배치하는 도중의 여러 키»로 잴 때마다 상자 안 스타일을 통째로 다시 계산하고 안쪽 컨테이너가
 *    또 곱으로 돈다 — 시료 칸 하나 펼치기가 레이아웃 30초 · 렌더러 7GB 였다(노드 190개). 이 끈은 «배치가 끝난» 키를 한 번 재서
 *    px 로 건다 — 상자 키가 바뀔 때(창 · 서랍 · 열 접기)만 다시 잰다. 렌더 상태가 아니라 리렌더가 없다.
 * ⚠ 내용 상자 키(패딩 뺀 값)다 — `100cqh` 와 같은 뜻. 같은 키(반올림 px)면 다시 쓰지 않는다. 0(숨김)이면 마지막 값을 둔다.
 *    재기 전 첫 배치는 CSS 쪽 `var(이름, 대체값)` 의 대체값이다(관찰 첫 알림이 그리기 전에 와서 화면에는 잰 값이 선다).
 * 🔴 대체값에 `cqh` 를 다시 쓰지 마라 — 마운트(= 펼치기)마다 그 비싼 길을 한 번 탄다.
 */
export function useBoxHeightVar<E extends HTMLElement>(name: `--${string}`): RefCallback<E> {
  return useCallback(
    (el: E | null) => {
      if (el === null || typeof ResizeObserver === 'undefined') return
      let last = -1
      const observer = new ResizeObserver((entries) => {
        const height = Math.round(entries[0]?.contentRect.height ?? 0)
        if (height <= 0 || height === last) return
        last = height
        el.style.setProperty(name, `${height}px`)
      })
      observer.observe(el)
      return () => observer.disconnect()
    },
    [name],
  )
}

/**
 * **상자 폭이 문턱 이하이면 표지 속성 하나를 단다**(소모품 칸 «좁음» — 종전 `@container monitoring-supply (max-width: 32rem)`).
 *
 * 🔴 **컨테이너 쿼리로 되돌리지 마라.** 소모품 칸에 그 폭 조건이 걸려 있는 동안은 옆 장비 칸이 바뀔 때마다(칩 전환 · 칸 펼치기)
 *    소모품 판 전체(노드 1,400여 개)를 다시 계산해 한 번에 0.25~0.8초를 먹었다(같은 컨테이너라도 묻는 규칙이 없으면 10~40ms).
 *    이 끈은 폭이 문턱을 «넘을 때만» 속성을 바꾼다 — 그때만 스타일이 다시 돈다.
 * ⚠ 문턱은 `rem` 이다(뿌리 글자 크기 기준 · 종전 쿼리와 같은 뜻). 폭은 내용 상자 폭이다.
 * ⚠ React 가 관리하지 않는 속성이라 리렌더가 지우지 않는다. 관찰이 없는 곳(jsdom)에서는 달지 않는다(넓은 모양).
 */
export function useNarrowFlag<E extends HTMLElement>(
  attribute: `data-${string}`,
  maxWidthRem: number,
): RefCallback<E> {
  return useCallback(
    (el: E | null) => {
      if (el === null || typeof ResizeObserver === 'undefined') return
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0
        if (width <= 0) return
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        el.toggleAttribute(attribute, width <= maxWidthRem * rem)
      })
      observer.observe(el)
      return () => {
        observer.disconnect()
        el.removeAttribute(attribute)
      }
    },
    [attribute, maxWidthRem],
  )
}
